import { Injectable } from '@angular/core';
import { BrowserQRCodeSvgWriter } from '@zxing/library';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class QrCodeService {
  private codeWriter = new BrowserQRCodeSvgWriter();

  constructor(private logger: LoggerService) {}

  /**
   * Generates a QR code as SVG string
   * @param content The content to encode in the QR code
   * @param size The size of the QR code (default: 200)
   * @returns SVG string representation of the QR code
   */
  generateQRCode(content: string, size: number = 200): string {
    try {
      const svgElement = this.codeWriter.write(content, size, size);

      if (!svgElement) {
        throw new Error('QR code writer returned null/undefined');
      }

      // Convert SVG element to string
      if (svgElement instanceof SVGElement) {
        return new XMLSerializer().serializeToString(svgElement);
      } else {
        return String(svgElement);
      }
    } catch (error) {
      return this.generateFallbackQR(content, size);
    }
  }

  /**
   * Generates a fallback QR-like placeholder
   */
  private generateFallbackQR(content: string, size: number): string {
    return `
      <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="white" stroke="black" stroke-width="2"/>
        <text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-size="12" fill="black">
          QR Code
        </text>
        <text x="50%" y="60%" text-anchor="middle" dominant-baseline="middle" font-size="8" fill="gray">
          ${content.substring(0, 20)}...
        </text>
      </svg>
    `;
  }

  /**
   * Generates a QR code as data URL for use in img src
   * @param content The content to encode in the QR code
   * @param size The size of the QR code (default: 200)
   * @returns Data URL string
   */
  generateQRCodeDataUrl(content: string, size: number = 200): string {
    try {
      const svg = this.generateQRCode(content, size);
      const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(svgBlob);
      return url;
    } catch (error) {
      this.logger.error('Error generating QR code data URL:', error);
      throw new Error('Failed to generate QR code data URL');
    }
  }

  /**
   * Generates a QR code as base64 encoded PNG
   * @param content The content to encode in the QR code
   * @param size The size of the QR code (default: 200)
   * @returns Promise that resolves to base64 PNG string
   */
  async generateQRCodeBase64(content: string, size: number = 200): Promise<string> {
    try {
      const svg = this.generateQRCode(content, size);
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      canvas.width = size;
      canvas.height = size;

      return new Promise((resolve, reject) => {
        img.onload = () => {
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');
            resolve(dataUrl);
          } else {
            reject(new Error('Could not get canvas context'));
          }
        };
        img.onerror = () => reject(new Error('Failed to load SVG image'));
        img.src = 'data:image/svg+xml;base64,' + btoa(svg);
      });
    } catch (error) {
      this.logger.error('Error generating QR code base64:', error);
      throw new Error('Failed to generate QR code base64');
    }
  }
}
